import { useEffect, useState } from 'react';
import { useIsMounted } from '@toss/react';
import { useRouter } from 'next/router';
import { appendTrackingId, appendUTMParams, parseTrackingId, TRACKING_ID_PARAM } from '@/utils/analytics';
import { cn } from '@/utils/tailwind.util';

/**
 * 임베드를 허용하는 문의 폼 origin.
 *
 * `/embed/sales` 는 `webFormUrl` 을 쿼리로도 받는 공개 라우트라, 검증 없이 iframe 에
 * 실으면 누구나 우리 도메인 안에 임의 페이지를 띄울 수 있다(피싱). 실제로 쓰는 폼은
 * 시트가 주는 이 둘뿐이다 — ko 는 promotion, 나머지 로케일은 tally.
 */
const TRUSTED_FORM_ORIGINS = ['https://promotion.zep.us', 'https://tally.so'] as const;
export const isTrustedFormUrl = (value: string) => {
  try {
    return (TRUSTED_FORM_ORIGINS as readonly string[]).includes(new URL(value).origin);
  } catch {
    return false;
  }
};

/**
 * 폼이 높이를 알려오기 전까지 쓰는 기본 높이.
 *
 * cross-origin 이라 실제 콘텐츠 높이를 읽을 수 없어, 도입문의 폼(11필드) 레이아웃 기준으로
 * 넉넉히 잡는다. 모자라면 iframe 안에 스크롤이 생겨 페이지 스크롤과 이중이 되고, 남는 높이는
 * 폼이 자기 배경으로 채우므로(`.pf-wrap { min-height: 100vh }`) 뚫려 보이지 않는다.
 *
 * 폼은 **자기 폭**이 480px 이하면 필드를 1열로 쌓아 훨씬 길어진다. iframe 폭은 뷰포트에서
 * 좌우 패딩을 뺀 값이라, 640px 뷰포트부터는 두 사용처 모두 2열이 된다고 보고 낮춘다.
 */
const FALLBACK_HEIGHT_CLASS = 'h-[1500px] min-[640px]:h-[1250px]';
interface Props {
  webFormUrl: string;
  title?: string;
  className?: string;
}
export function InquiryFormEmbed({
  webFormUrl,
  title,
  className
}: Props) {
  const mounted = useIsMounted();
  const router = useRouter();

  // 문의 CTA 가 내부 라우팅으로 넘겨준 클릭 식별자. 클릭 없이 인라인 임베드된 사용처
  // (랜딩 Pricing)에는 없다. 공개 URL 로 오는 값이라 형식이 맞는 것만 폼에 실어 보낸다.
  const trackingId = parseTrackingId(router.query[TRACKING_ID_PARAM]);

  // 지금 폼은 높이를 보내지 않아 항상 폴백으로 남지만, 폼 쪽에 통지를 붙이면 여기를
  // 고치지 않고도 정확히 맞는다. 걷어낸 salesmap 로더의 계약을 유지한 것(출처 검증은 추가).
  const [reportedHeight, setReportedHeight] = useState<number | null>(null);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!(TRUSTED_FORM_ORIGINS as readonly string[]).includes(event.origin)) return;
      const height = (event.data as {
        height?: unknown;
      } | null)?.height;
      if (typeof height === 'number' && height > 0) {
        setReportedHeight(height);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // URL 가공(UTM·tracking_id)이 window 를 읽는다. 서버에서 가공 전 URL 로 렌더되면
  // 하이드레이션 미스매치 + 잘못된 URL 로 폼이 먼저 로드된다.
  if (!mounted) return null;

  // 소비처가 검증을 빠뜨려도 신뢰하지 않는 출처는 렌더하지 않는다.
  if (!isTrustedFormUrl(webFormUrl)) return null;
  return <iframe src={appendTrackingId(appendUTMParams(webFormUrl), trackingId)} title={title} className={cn('w-full border-0', reportedHeight === null && FALLBACK_HEIGHT_CLASS, className)} style={reportedHeight === null ? undefined : {
    height: reportedHeight
  }} data-sentry-component="InquiryFormEmbed" data-sentry-source-file="InquiryFormEmbed.tsx" />;
}